<script setup lang="ts">
import { onMounted, watch } from 'vue';
import { BDialog, BBtn } from '@/design-system';
import IconCheck from '@/assets/img/icon-check.svg?component';
import { useTranslation } from '@/composables';

type PropsType = {
    modelValue: boolean;
    storageKey: string;
    title: string;
    successMessage: string;
    nextStepMessage: string;
    actionLabel?: string;
    showAction?: boolean;
};

const props = withDefaults(defineProps<PropsType>(), {
    actionLabel: 'common.continue',
    showAction: true,
});

const emit = defineEmits<{
    'update:modelValue': [value: boolean];
    action: [];
    close: [];
}>();

const { t } = useTranslation();

onMounted(() => {
    if (props.modelValue) {
        const hasSeenBefore = localStorage.getItem(props.storageKey) === 'true';
        if (hasSeenBefore) {
            emit('update:modelValue', false);
        }
    }
});

watch(
    () => props.modelValue,
    newValue => {
        if (newValue) {
            const hasSeenBefore = localStorage.getItem(props.storageKey) === 'true';
            if (hasSeenBefore) {
                emit('update:modelValue', false);
            }
        }
    },
);

const handleClose = () => {
    localStorage.setItem(props.storageKey, 'true');
    emit('update:modelValue', false);
    emit('close');
};

const handleAction = () => {
    localStorage.setItem(props.storageKey, 'true');
    emit('action');
    emit('update:modelValue', false);
};
</script>

<template>
    <BDialog
        :id="`onboarding-${storageKey}`"
        :model-value="modelValue"
        :max-width="560"
        hide-actions
        @cancel="handleClose"
    >
        <template #body>
            <div class="onboarding-modal-content">
                <div class="onboarding-icon">
                    <IconCheck style="min-width: 150px; height: auto" />
                </div>

                <div class="onboarding-messages">
                    <p class="success-message">
                        {{ successMessage }}
                    </p>
                    <p class="next-step-message">
                        {{ nextStepMessage }}
                    </p>
                </div>

                <div class="onboarding-actions">
                    <BBtn
                        variant="text"
                        @click="handleClose"
                    >
                        {{ t('common.close') }}
                    </BBtn>
                    <BBtn
                        v-if="showAction"
                        color="primary"
                        @click="handleAction"
                    >
                        {{ actionLabel }}
                    </BBtn>
                </div>
            </div>
        </template>
    </BDialog>
</template>

<style lang="scss" scoped>
.onboarding-modal-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-block: 0 24px;
    text-align: center;
}

.onboarding-icon {
    margin-bottom: 24px;
}

.onboarding-messages {
    margin-bottom: 32px;
    max-width: 440px;

    .success-message {
        font-size: 1.1rem;
        font-weight: 500;
        margin-bottom: 12px;
        color: rgb(var(--v-theme-grey-800));
    }

    .next-step-message {
        font-size: 0.95rem;
        color: rgb(var(--v-theme-grey-500));
        line-height: 1.5;
    }
}

.onboarding-actions {
    display: flex;
    gap: 12px;
    justify-content: center;
}
</style>
